import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function NewYearCombo419() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pulseCheckout, setPulseCheckout] = useState(false);
  const navigate = useNavigate();
  const checkoutBtnRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/products`)
      .then((res) => {
        // keep stronger filtering similar to FirstOrderCombo
        const comboKeywords = /\b(combo|bundle|gift|hamper|set|offer|kit|newyear|combo419)\b/i;

        const isComboProduct = (p) => {
          if (p.isCombo === true || p.isCombo419 === true || p.combo419 === true) return true;

          const name = (p.name || "").toString();
          const desc = (p.description || "").toString();
          const tags = Array.isArray(p.tags) ? p.tags.join(" ") : (p.tags || "").toString();
          const catName = (p.Category && (p.Category.name || "")) || "";
          const catSlug = (p.Category && (p.Category.slug || "")) || "";

          const haystack = [name, desc, tags, catName, catSlug].join(" ");
          return comboKeywords.test(haystack);
        };

        const filtered = Array.isArray(res.data) ? res.data.filter((p) => !isComboProduct(p)) : [];
        setProducts(filtered);
      })
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, []);

  const toggleSelect = (product) => {
    const id = product.id ?? product._id;
    if (selected.find((p) => (p.id ?? p._id) === id)) {
      setSelected(selected.filter((p) => (p.id ?? p._id) !== id));
      return;
    }

    if (selected.length >= 4) {
      toast.error("❗ You can select only 4 products");
      return;
    }

    setSelected([...selected, product]);
  };

  // Scroll to checkout + pulse when 4 selected
  useEffect(() => {
    if (selected.length === 4) {
      setTimeout(() => {
        checkoutBtnRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 120);

      setPulseCheckout(true);
      const t = setTimeout(() => setPulseCheckout(false), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [selected.length]);

  const goToCart = async () => {
    if (selected.length !== 4) {
      return toast.warn("Please select exactly 4 products!");
    }

    const comboItems = selected.map((p) => ({ productId: p.id ?? p._id, quantity: 1 }));

    localStorage.setItem("comboProducts", JSON.stringify(comboItems));
    localStorage.setItem("isNewYearCombo419", "true");
    // mirror the 333 flow: also set offer419 flag so Cart.js quick-apply recognizes it
    localStorage.setItem("offer419", "true");

    const token = localStorage.getItem("token");

    if (token) {
      try {
        for (let item of comboItems) {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/cart/add`,
            { productId: item.productId, quantity: 1, type: "PRODUCT" },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }

        toast.success("🎉 New Year Combo applied!");
        navigate("/cart?combo=419");
        return;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error adding combo to backend cart:", err);
        toast.error("Error adding combo to cart!");
      }
    }

    toast.info("Login to complete New Year Offer");
    navigate("/login?redirect=/cart&combo=419");
  };

  if (loading)
    return (
      <div className="w-full text-center p-10 text-xl font-bold">Loading products...</div>
    );

  return (
    <div className="p-4 sm:p-8 min-h-screen bg-gradient-to-b from-orange-50 to-amber-50">
      {/* Title */}
      <motion.div initial={{ opacity: 0, y: -25 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-orange-600 drop-shadow">🎆 New Year Mega Offer</h1>
        <p className="text-gray-700 mt-2 font-medium text-base sm:text-lg">
          Choose <b>any 4</b> products for only <span className="text-green-600 font-bold">₹419</span>
        </p>
        <p className="text-sm text-gray-500 mt-1">Selected: {selected.length}/4</p>
      </motion.div>

      {/* Product Card Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {products.map((product) => {
          const id = product.id ?? product._id;
          const isSelected = selected.some((p) => (p.id ?? p._id) === id);

          return (
            <motion.div
              key={id}
              onClick={() => toggleSelect(product)}
              whileTap={{ scale: 0.97 }}
              className={`relative border rounded-xl bg-white shadow-md p-4 cursor-pointer transition-all hover:shadow-lg
                ${isSelected ? "border-green-600 ring-2 ring-green-400 shadow-xl scale-[1.03]" : "border-gray-200"}
              `}
            >
              {/* Badge */}
              <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">New Year</div>

              {/* Selection check ✓ */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-3 right-3 bg-green-600 text-white w-7 h-7 flex items-center justify-center rounded-full shadow-md"
                >
                  ✅
                </motion.div>
              )}

              {/* Product Image (mobile-first, prevents overflow) */}
              <div className="w-full overflow-hidden rounded-lg mb-3 bg-white flex items-center justify-center">
                <img
                  src={product.image}
                  alt={product.name}
                  loading="lazy"
                  className="block w-full max-w-full h-auto max-h-40 sm:max-h-44 md:max-h-48 lg:max-h-56 object-contain"
                  style={{ width: "100%", height: "auto" }}
                />
              </div>

              {/* Product Info */}
              <h2 className="text-sm sm:text-base font-semibold text-gray-800 mb-1 truncate">{product.name}</h2>
              <p className="text-xs text-gray-500 line-through mb-1">₹{product.price}</p>
              <p className="text-green-700 text-sm font-semibold">₹105 in Combo</p>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom Action Bar */}
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 w-full bg-white/90 backdrop-blur-md shadow-lg py-3 px-6 flex justify-between items-center"
      >
        <span className="font-semibold text-gray-700">
          Selected: <b>{selected.length}/4</b>
        </span>

        <motion.button
          ref={checkoutBtnRef}
          whileTap={{ scale: 0.94 }}
          animate={pulseCheckout ? { scale: [1, 1.06, 1], boxShadow: "0 8px 30px rgba(234,88,12,0.18)" } : {}}
          transition={{ duration: 0.9, ease: "easeInOut" }}
          onClick={goToCart}
          disabled={selected.length !== 4}
          className={`px-6 py-2 rounded-lg font-semibold text-danger transition-all shadow-md
            ${selected.length === 4 ? "bg-orange-600 hover:bg-orange-700" : "bg-gray-400 cursor-not-allowed"}
          `}
        >
          Checkout ₹419
        </motion.button>
      </motion.div>
    </div>
  );
}

